import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  useRowSelect,
  usePagination,
} from "react-table";
import React from "react";
import { useNavigate } from "react-router-dom";
import { IconContext } from "react-icons";
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaAngleLeft,
  FaAngleRight,
  FaSearch,
} from "react-icons/fa";
import { FilterIcon, XIcon } from "@heroicons/react/solid";
import customAxios from "middleware/customAxios";

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <input
        className="bg-orange-100 border-orange-300 text-orange-500 focus:ring-0 cursor-pointer"
        type="checkbox"
        ref={resolvedRef}
        {...rest}
      />
    );
  }
);

export default function DataGrid({
  searchable,
  api,
  second,
  showFooter,
  showCard,
  cellFunction,
  x_columns,
  testData,
  setSelectedRow,
  setSelectedRows,
  selectedRow,
  showVerticalDividers,
  path,
  height,
  selectable,
  showHeader,
  preSelectedItems,
  alignRow,
  alignHeader,
  // Pagination props
  paginationModel,
  onPaginationChange,
  defaultPageSize = 10,
  pageSizeOptions = [10, 20, 30, 40, 50],
  paginationMode = "client",
  // Filter props
  showFilterButton = false,
  filterOptions = [],
  onFilterApply,
  activeFilters = [],
  externalParams, // new prop for external filters
  onDataLoaded, // called when API fetch completes (e.g. to hide filter spinner)
}) {
  // Data states
  const [tableData, setTableData] = useState({});
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  // Search state (separate from params now)
  const [searchValue, setSearchValue] = useState("");

  // Filter UI state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempActiveFilters, setTempActiveFilters] = useState([]);

  // Internal pagination for uncontrolled mode
  const [internalPagination, setInternalPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Client-side (testData) page index - we control this so Next/Previous actually change the visible page
  const [clientPageIndex, setClientPageIndex] = useState(0);

  // Use controlled or internal pagination
  const currentPagination = paginationModel || internalPagination;

  // When server-side or parent controls pagination, we pass state. Otherwise let the table own pagination.
  const isPaginationControlled = paginationMode === "server" || !!onPaginationChange;

  // Reset pagination when external filters change
  useEffect(() => {
    if (!externalParams) return;

    if (onPaginationChange) {
      onPaginationChange(0, currentPagination.pageSize);
    } else {
      setInternalPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [externalParams, onPaginationChange, currentPagination.pageSize]);

  // Build request parameters for API call
  const requestParams = useMemo(() => {
    const { page: _, limit: externalLimit, ...filterParams } = externalParams || {};
    const limitNum =
      externalLimit != null && externalLimit !== ""
        ? Math.max(1, parseInt(externalLimit, 10) || currentPagination.pageSize)
        : currentPagination.pageSize;

    return {
      page: currentPagination.pageIndex + 1,
      limit: limitNum,
      search: searchValue || null,
      start: filterParams.start || "2021-01-01",
      end: filterParams.end || "2024-12-12",
      offset: 0,
      outlet: filterParams.outlet || null,
      ...filterParams,
    };
  }, [
    currentPagination.pageIndex,
    currentPagination.pageSize,
    searchValue,
    externalParams,
  ]);

  // Set loading to false when using testData (no API)
  useEffect(() => {
    if (!api && testData) {
      setLoading(false);
    }
  }, [api, testData]);

  // Fetch data from API
  useEffect(() => {
    if (!api) return;

    const urlParams = new URLSearchParams();
    Object.entries(requestParams).forEach(([key, value]) => {
      if (value == null || value === "") return;
      if (typeof value === "object") return; // never send [object Object]
      urlParams.append(key, String(value));
    });

    setLoading(true);
    customAxios
      .get(api + (api.includes("?") ? "&" : "?") + urlParams.toString())
      .then((res) => {
        // Support both: (1) customAxios interceptor returns response.data so res = { data, columns, meta };
        // (2) raw axios so res.data = { data, columns, meta }. Never store full meta to avoid leaking pagination JSON.
        const body = res?.data ?? {};
        const isPayloadObject = body && typeof body === "object" && !Array.isArray(body) && ("data" in body || "meta" in body);
        const data = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
        const cols = (isPayloadObject ? body.columns : null) ?? res?.columns ?? [];
        const meta = (isPayloadObject ? body.meta : null) ?? res?.meta;
        const total = typeof meta === "number" ? meta : (meta?.total ?? 0);
        setTableData({ data, columns: cols, total });
        setColumns(cols);
        setRowData(data);
        setTotalRows(Number(total) || data.length);
        setLoading(false);
        onDataLoaded?.({ data, total: Number(total) || data.length, columns: cols });
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        onDataLoaded?.();
      });
  }, [requestParams, api]);

  // React Table setup
  let navigate = useNavigate();
  const safePageSizeForTable = Number(currentPagination?.pageSize) || defaultPageSize || 10;

  const data = testData || [];

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter },
  } = useTable(
    {
      columns: x_columns != null ? x_columns : columns,
      data: api != null ? rowData : testData || [],
      initialState: {
        pageIndex: 0,
        pageSize: safePageSizeForTable,
      },
      ...(isPaginationControlled
        ? {
            state: {
              pageIndex: currentPagination.pageIndex ?? 0,
              pageSize: currentPagination.pageSize ?? safePageSizeForTable,
            },
          }
        : {}),
      manualPagination: paginationMode === "server",
      pageCount:
        paginationMode === "server"
          ? Math.ceil((Number(totalRows) || 0) / safePageSizeForTable) || 1
          : undefined,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (selectable) {
        hooks.visibleColumns.push((columns) => [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox
                  {...getToggleAllRowsSelectedProps()}
                />
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...columns,
        ]);
      }
    }
  );

  // Row selection handler
  const rowSelectHandler = (row) => {
    if (selectable && path) {
      navigate(path + row.original.id);
    }
    if (setSelectedRow) setSelectedRow(row.original);
    if (setSelectedRows) setSelectedRows(selectedFlatRows);
  };

  // Filter UI handlers
  const handleFilterCheckboxChange = (value) => {
    setTempActiveFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleApplyFilters = () => {
    if (onFilterApply) {
      onFilterApply(tempActiveFilters);
    }
    setIsFilterOpen(false);
  };

  const handleClearFilters = () => {
    setTempActiveFilters([]);
    if (onFilterApply) {
      onFilterApply([]);
    }
    setIsFilterOpen(false);
  };

  const handleOpenFilterDropdown = () => {
    setTempActiveFilters([...activeFilters]);
    setIsFilterOpen(!isFilterOpen);
  };

  const handleRemoveFilter = (valueToRemove) => {
    const newFilters = activeFilters.filter((v) => v !== valueToRemove);
    if (onFilterApply) {
      onFilterApply(newFilters);
    }
  };

  // Click outside to close filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isFilterOpen &&
        !event.target.closest(".filter-dropdown") &&
        !event.target.closest(".filter-button")
      ) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (onPaginationChange) {
      onPaginationChange(newPage, currentPagination.pageSize);
    } else {
      setInternalPagination((prev) => ({
        ...prev,
        pageIndex: newPage,
      }));
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    if (onPaginationChange) {
      onPaginationChange(0, newPageSize);
    } else {
      setInternalPagination({
        pageIndex: 0,
        pageSize: newPageSize,
      });
    }
  };

  // Search handler with page reset
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (onPaginationChange) {
      onPaginationChange(0, currentPagination.pageSize);
    } else {
      setInternalPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
    if (!isPaginationControlled) setClientPageIndex(0);
  };

  // Display range for pagination info (support both api rowData and testData)
  const dataLength = api != null ? rowData.length : (testData || []).length;
  const totalRowCount = Number(totalRows) || dataLength || 0;
  const effectivePageSize = isPaginationControlled
    ? (Number(currentPagination?.pageSize) || defaultPageSize || 10)
    : (Number(pageSize) || defaultPageSize || 10);
  const effectivePageIndex = isPaginationControlled
    ? (currentPagination.pageIndex ?? 0)
    : clientPageIndex;
  const safePageSize = Number(currentPagination?.pageSize) || defaultPageSize || 10;
  const startRow =
    totalRowCount > 0 ? effectivePageIndex * effectivePageSize + 1 : 0;
  const endRow = Math.min(startRow + effectivePageSize - 1, totalRowCount);
  const totalPages =
    Math.max(1, Math.ceil(totalRowCount / effectivePageSize) || 1);

  // Keep client page index in bounds when total pages shrinks (e.g. after filter/search)
  useEffect(() => {
    if (!isPaginationControlled && clientPageIndex >= totalPages && totalPages > 0) {
      setClientPageIndex(totalPages - 1);
    }
  }, [isPaginationControlled, clientPageIndex, totalPages]);

  // For client-side pagination, explicitly slice rows so we always show only the current page.
  const rowsToRender =
    paginationMode === "server"
      ? page
      : rows.slice(
          effectivePageIndex * effectivePageSize,
          effectivePageIndex * effectivePageSize + effectivePageSize
        );

  return (
    <div
      className={`flex flex-col space-y-2 ${height != null ? height : ""
        } w-full justify-between items-start`}
    >
      {/* Top bar with search and filter */}
      <div className="flex w-full justify-between items-center">
        <div className="flex items-center space-x-3">
          {/* Search */}
          {searchable && (
            <div className="h-8 w-64 bg-slate-100 rounded-md flex justify-center px-4 p-2 space-x-3 items-center">
              <IconContext.Provider value={{ className: "text-slate-400" }}>
                <FaSearch />
              </IconContext.Provider>
              <input
                type="text"
                className="w-full text-sm h-full bg-transparent text-black dark:text-white focus:outline-none"
                placeholder="Quick Search"
                onChange={handleSearchChange}
                value={searchValue}
              />
            </div>
          )}

          {/* Built‑in Filter Button (optional) */}
          {showFilterButton && filterOptions.length > 0 && (
            <div className="relative">
              <button
                onClick={handleOpenFilterDropdown}
                className="filter-button flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <FilterIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter</span>
                {activeFilters.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                    {activeFilters.length}
                  </span>
                )}
              </button>

              {/* Filter Dropdown */}
              {isFilterOpen && (
                <div className="filter-dropdown absolute top-12 left-0 z-50 w-64 bg-white rounded-lg shadow-xl border border-gray-200">
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      Filter
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {filterOptions.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={tempActiveFilters.includes(option.value)}
                            onChange={() =>
                              handleFilterCheckboxChange(option.value)
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                          />
                          <span
                            className={`text-sm px-2 py-1 rounded-full bg-${option.color}-100 text-${option.color}-800`}
                          >
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-end space-x-2 mt-4 pt-2 border-t border-gray-100">
                      <button
                        onClick={handleClearFilters}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                      >
                        Clear All
                      </button>
                      <button
                        onClick={handleApplyFilters}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 transition-colors"
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Active filters display */}
        {activeFilters.length > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">Active filters:</span>
            <div className="flex flex-wrap gap-2">
              {activeFilters.map((filter) => {
                const option = filterOptions.find((opt) => opt.value === filter);
                return (
                  <span
                    key={filter}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${option?.color
                      }-100 text-${option?.color}-800`}
                  >
                    {option?.label}
                    <button
                      onClick={() => handleRemoveFilter(filter)}
                      className="ml-1 hover:bg-opacity-20 rounded-full p-0.5"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
              <button
                onClick={handleClearFilters}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 w-full justify-start bg-white overflow-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
          </div>
        ) : (
          <table className={"w-full"} {...getTableProps()}>
            <thead
              className={` ${showHeader ? "" : "text-white h-0 top-5"} w-full`}
            >
              {headerGroups.map((headerGroup) => (
                <tr
                  className={`font-medium`}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map((column) => (
                    <th
                      className={`text-sm text-black ${second ? "border" : "border-b"
                        } pl-3 py-2 bg-white font-bold sticky top-0 ${alignHeader == "center" ? "text-center" : "text-left"
                        } w-fit`}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <span
                        className={`text-sm ${showHeader ? "" : "invisible"
                          } text-start ${alignHeader == "center" ? "text-center" : "text-left"
                          }`}
                      >
                        {column.render("Header")}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody
              className="divide-y divide-slate-100"
              {...getTableBodyProps()}
            >
              <AnimatePresence>
                {rowsToRender.length > 0 ? (
                  rowsToRender.map((row, i) => {
                    prepareRow(row);
                    return (
                      <motion.tr
                        key={row.id || i}
                        onClick={() => rowSelectHandler(row)}
                        className={`
                        ${showVerticalDividers && "divide-x"}  
                        ${selectedRow == row.original ? "bg-slate-200" : ""
                          }
                        ${selectedFlatRows.includes(row.values)
                            ? "bg-orange-100"
                            : ""
                          }
                        pb-1 pl-3 justify-start items-start hover:bg-slate-100/40 hover:dark:bg-slate-200/10 bg-opacity-75 cursor-pointer
                      `}
                        {...row.getRowProps({
                          layoutTransition: {
                            type: "spring",
                            damping: 50,
                            stiffness: 100,
                          },
                          exit: { opacity: 0, maxHeight: 0 },
                          enter: { opacity: 0, maxHeight: 0 },
                        })}
                      >
                        {row.cells.map((cell) => (
                          <td
                            className={`text-sm pl-3 py-2 ${alignRow == "center" ? "text-start" : ""
                              }`}
                            {...cell.getCellProps()}
                          >
                            {cellFunction
                              ? cellFunction(cell)
                              : cell.render("Cell")}
                          </td>
                        ))}
                      </motion.tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={headerGroups[0]?.headers?.length || 1}
                      className="text-center py-8 text-gray-500"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </AnimatePresence>
            </tbody>
            {showFooter && footerGroups && (
              <tfoot>
                {footerGroups.map((group) => (
                  <tr
                    className="border-t border-slate-400"
                    {...group.getFooterGroupProps()}
                  >
                    {group.headers.map((column) => (
                      <td className="p-2" {...column.getFooterProps()}>
                        {column.render("Footer")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        )}
      </div>

      {/* Pagination - show when there is data (api or testData) */}
      {rowsToRender.length > 0 && (
          <div className="flex flex-col md:flex-row justify-between items-center w-full bg-slate-100 rounded-xl px-4 py-2 gap-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <select
                className="w-16 h-8 rounded-md bg-white border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 px-1"
                value={effectivePageSize}
                onChange={(e) => {
                  const newSize = parseInt(e.target.value, 10);
                  const size = Number.isFinite(newSize) ? newSize : effectivePageSize;
                  if (isPaginationControlled) {
                    handlePageSizeChange(size);
                  } else {
                    setPageSize(size);
                    gotoPage(0);
                    setClientPageIndex(0);
                  }
                }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-1">
              <span className="text-sm text-gray-600">
                {startRow} - {endRow} of {totalRowCount}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <span className="text-sm text-gray-600 mr-2">
                Page {effectivePageIndex + 1} of {totalPages}
              </span>

              <button
                className="w-8 h-8 rounded-md bg-white border border-gray-200 text-black hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={() =>
                  isPaginationControlled
                    ? handlePageChange(0)
                    : setClientPageIndex(0)
                }
                disabled={effectivePageIndex === 0}
                title="First Page"
              >
                <IconContext.Provider value={{ className: "text-slate-600 mx-auto" }}>
                  <FaAngleDoubleLeft className="mx-auto" />
                </IconContext.Provider>
              </button>

              <button
                className="w-8 h-8 rounded-md bg-white border border-gray-200 text-black hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={() =>
                  isPaginationControlled
                    ? handlePageChange(effectivePageIndex - 1)
                    : setClientPageIndex((p) => Math.max(0, p - 1))
                }
                disabled={effectivePageIndex === 0}
                title="Previous Page"
              >
                <IconContext.Provider value={{ className: "text-slate-600 mx-auto" }}>
                  <FaAngleLeft className="mx-auto" />
                </IconContext.Provider>
              </button>

              <div className="flex items-center space-x-1 px-2">
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={effectivePageIndex + 1}
                  onChange={(e) => {
                    const pageNum = e.target.value ? parseInt(e.target.value, 10) - 1 : 0;
                    if (Number.isFinite(pageNum) && pageNum >= 0 && pageNum < totalPages) {
                      if (isPaginationControlled) {
                        handlePageChange(pageNum);
                      } else {
                        setClientPageIndex(pageNum);
                      }
                    }
                  }}
                  className="w-12 h-8 text-center rounded-md bg-white border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                />
              </div>

              <button
                className="w-8 h-8 rounded-md bg-white border border-gray-200 text-black hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={() =>
                  isPaginationControlled
                    ? handlePageChange(effectivePageIndex + 1)
                    : setClientPageIndex((p) => Math.min(p + 1, totalPages - 1))
                }
                disabled={effectivePageIndex + 1 >= totalPages}
                title="Next Page"
              >
                <IconContext.Provider value={{ className: "text-slate-600 mx-auto" }}>
                  <FaAngleRight className="mx-auto" />
                </IconContext.Provider>
              </button>

              <button
                className="w-8 h-8 rounded-md bg-white border border-gray-200 text-black hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={() =>
                  isPaginationControlled
                    ? handlePageChange(totalPages - 1)
                    : setClientPageIndex(totalPages - 1)
                }
                disabled={effectivePageIndex + 1 >= totalPages}
                title="Last Page"
              >
                <IconContext.Provider value={{ className: "text-slate-600 mx-auto" }}>
                  <FaAngleDoubleRight className="mx-auto" />
                </IconContext.Provider>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}